import React, { useContext, useState, useEffect, useCallback, memo, useRef, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  TablePagination,
  InputAdornment,
  Tooltip,
  CircularProgress,
  Box,
  IconButton,
  Snackbar,
  Alert,
  Slide,
  Chip,
} from '@mui/material';

import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import GroupsIcon from '@mui/icons-material/Groups';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';

import { db } from '../firebase';
import {
  doc,
  updateDoc,
  collection,
  getDocs,
  getDoc,
  onSnapshot,
  query,
  where,
  setDoc,
} from 'firebase/firestore';

import { UserContext } from '../App';
import { useSession } from '../utils/SessionContext';
import { saveAs } from 'file-saver';

import BGElida from '../img/BGElida.png';
import SmartLabLogo from '../img/SmartLabLogo.png';

import NEXUSI from '../img/NEXUSI.jpg';
import NEXUSII from '../img/NEXUSII.jpg';
import NEXUSIII from '../img/NEXUSIII.jpg';
import NEXUSIV from '../img/NEXUSIV.jpg';

import SANDBOXI from '../img/SANDBOXI.jpg';
import SANDBOXII from '../img/SANDBOXII.jpg';
import SANDBOXIII from '../img/SANDBOXIII.jpg';
import SANDBOXIV from '../img/SANDBOXIV.jpg';

import RAISELABI from '../img/RAISELABI.jpg';
import RAISELABII from '../img/RAISELABII.jpg';
import RAISELABIII from '../img/RAISELABIII.jpg';
import RAISELABIV from '../img/RAISELABIV.jpg';

const SlideUpTransition = (props) => <Slide {...props} direction="up" />;

const normalizeRfid = (value) => String(value || '').trim().toLowerCase();
const normalizeText = (value) => String(value || '').trim();

const getDateValue = (value) => {
  if (!value) return null;

  try {
    if (typeof value?.toDate === 'function') {
      const dateValue = value.toDate();
      return Number.isNaN(dateValue.getTime()) ? null : dateValue;
    }

    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : value;
    }

    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  } catch (error) {
    console.error('Timestamp parse error:', error);
    return null;
  }
};

const getTimestampMillis = (value) => {
  const parsed = getDateValue(value);
  return parsed ? parsed.getTime() : null;
};

const formatTimestampToTime = (value) => {
  const dateValue = getDateValue(value);
  if (!dateValue) return '-';

  try {
    return dateValue.toLocaleTimeString('en-US', {
      timeZone: 'Asia/Manila',
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch (error) {
    console.error('Timestamp format error:', error);
    return '-';
  }
};

const escapeCsvValue = (value) => {
  const text = String(value ?? '');
  if (/[",\n]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
};

const MemoizedRow = memo(({ student }) => (
  <TableRow hover sx={{ '&:hover': { backgroundColor: 'action.hover' } }}>
    <TableCell sx={{ fontFamily: 'Poppins, sans-serif' }}>{`${student.firstName} ${student.lastName}`}</TableCell>
    <TableCell sx={{ fontFamily: 'Poppins, sans-serif' }}>{student.course || '-'}</TableCell>
    <TableCell sx={{ fontFamily: 'Poppins, sans-serif' }}>{student.yearLevel || '-'}</TableCell>
    <TableCell sx={{ fontFamily: 'Poppins, sans-serif' }}>{student.section || '-'}</TableCell>
    <TableCell sx={{ fontFamily: 'Poppins, sans-serif' }}>
      {student.status && student.status !== '-' ? (
        <Chip
          label={student.status}
          size="small"
          sx={{
            fontFamily: 'Poppins, sans-serif',
            fontWeight: 700,
            color: student.status === 'Late' ? '#fff' : '#0f56b3',
            bgcolor: student.status === 'Late' ? '#b90d0d' : '#e8f1ff',
            border: student.status === 'Late' ? 'none' : '1px solid rgba(15,86,179,0.18)',
          }}
        />
      ) : (
        '-'
      )}
    </TableCell>
    <TableCell sx={{ fontFamily: 'Poppins, sans-serif' }}>{formatTimestampToTime(student.timeIn)}</TableCell>
    <TableCell sx={{ fontFamily: 'Poppins, sans-serif' }}>{formatTimestampToTime(student.timeOut)}</TableCell>
  </TableRow>
));
MemoizedRow.displayName = 'MemoizedRow';

const LAB_BACKGROUND_MAP = {
  NEXUS: [NEXUSI, NEXUSII, NEXUSIII, NEXUSIV],
  'RAISE LAB': [RAISELABI, RAISELABII, RAISELABIII, RAISELABIV],
  'SAND BOX': [SANDBOXI, SANDBOXII, SANDBOXIII, SANDBOXIV],
};

const NewSession = () => {
  const { user } = useContext(UserContext);
  const { session, updateSession } = useSession();
  const navigate = useNavigate();
  const { sessionId } = useParams();

  const [, setMessage] = useState('');
  const [attendance, setAttendance] = useState([]);
  const [enrolledStudents, setEnrolledStudents] = useState([]);
  const [mode, setMode] = useState('Time In');

  const [openConfirm, setOpenConfirm] = useState(false);
  const [openEndConfirm, setOpenEndConfirm] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [loading, setLoading] = useState(true);
  const [sessionError, setSessionError] = useState('');
  const [retryCount, setRetryCount] = useState(0);

  const [attendanceModalOpen, setAttendanceModalOpen] = useState(false);

  const [currentTime, setCurrentTime] = useState({
    main: '00:00',
    seconds: '00',
    period: 'AM',
  });
  const [sessionTime, setSessionTime] = useState(0);
  const [lastStudentName, setLastStudentName] = useState('-');
  const [lastTapTime, setLastTapTime] = useState('00:00:00');

  const [toastOpen, setToastOpen] = useState(false);
  const [toastText, setToastText] = useState('');
  const [toastSeverity, setToastSeverity] = useState('success');

  const [selectedLab, setSelectedLab] = useState(session?.selectedLab || '');
  const [bgIndex, setBgIndex] = useState(0);
  const [sessionData, setSessionData] = useState(null);

  const attendanceUnsubscribeRef = useRef(null);
  const initializedRef = useRef(false);
  const scanBufferRef = useRef('');
  const scanTimeoutRef = useRef(null);
  const attendanceRef = useRef([]);
  const enrolledStudentsRef = useRef([]);

  const sessionCourse = sessionData?.course || session?.course || '';
  const sessionYearLevel = sessionData?.yearLevel || session?.yearLevel || '';
  const sessionSection = sessionData?.section || session?.section || '';
  const sessionSubject = sessionData?.subject || session?.subject || 'UNDERSTANDING THE SELF';
  const sessionProf = sessionData?.prof || session?.prof || '';
  const displayedLab = selectedLab || sessionData?.selectedLab || session?.selectedLab || '';

  const currentLabImages =
    LAB_BACKGROUND_MAP[displayedLab] ||
    (Array.isArray(sessionData?.labImages) && sessionData.labImages.length > 0
      ? sessionData.labImages
      : Array.isArray(session?.labImages) && session?.labImages.length > 0
        ? session.labImages
        : [BGElida]);

  const currentBgImage = currentLabImages[bgIndex] || currentLabImages[0] || BGElida;

  useEffect(() => {
    attendanceRef.current = attendance;
  }, [attendance]);

  useEffect(() => {
    enrolledStudentsRef.current = enrolledStudents;
  }, [enrolledStudents]);

  useEffect(() => {
    setBgIndex(0);
  }, [displayedLab]);

  useEffect(() => {
    if (!currentLabImages || currentLabImages.length <= 1) return undefined;

    const interval = setInterval(() => {
      setBgIndex((prev) => (prev + 1) % currentLabImages.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [currentLabImages]);

  useEffect(() => {
    const updateTime = () => {
      const now = new Date();

      const main = now.toLocaleTimeString('en-US', {
        timeZone: 'Asia/Manila',
        hour12: true,
        hour: '2-digit',
        minute: '2-digit',
      });

      const manilaNow = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Manila' }));
      const seconds = manilaNow.getSeconds().toString().padStart(2, '0');

      const period = now.toLocaleTimeString('en-US', {
        timeZone: 'Asia/Manila',
        hour12: true,
        hour: '2-digit',
      }).includes('PM')
        ? 'PM'
        : 'AM';

      setCurrentTime({
        main: main.replace(/\s?(AM|PM)$/i, ''),
        seconds,
        period,
      });
    };

    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!sessionId) return undefined;

    const interval = setInterval(() => setSessionTime((prev) => prev + 1), 1000);
    return () => clearInterval(interval);
  }, [sessionId]);

  useEffect(() => {
    setPage(0);
  }, [searchQuery]);

  const showToast = (text, severity = 'success') => {
    setToastText(text);
    setToastSeverity(severity);
    setToastOpen(true);
  };

  const formatTime = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s
      .toString()
      .padStart(2, '0')}`;
  };

  const formatDayOnly = () =>
    new Date().toLocaleDateString('en-US', {
      timeZone: 'Asia/Manila',
      weekday: 'long',
    });

  const formatFullDate = () =>
    new Date().toLocaleDateString('en-US', {
      timeZone: 'Asia/Manila',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });

  const getSectionDisplay = (course, yearLevel, section) => {
    const courseMap = {
      'BS Information Technology': 'BSIT',
      'BS Computer Science': 'BSCS',
      'BS Engineering': 'BSE',
    };

    const yearMap = {
      '1st Year': '1',
      '2nd Year': '2',
      '3rd Year': '3',
      '4th Year': '4',
    };

    const courseAbbreviation = courseMap[course] || course || '-';
    const yearNumber = yearMap[yearLevel] || yearLevel || '-';
    return `${courseAbbreviation} - ${yearNumber}${section || ''}`;
  };

  const getProfessorDisplay = (profName) => {
    const cleanName = normalizeText(profName);
    if (!cleanName) return 'Prof.';
    if (/^prof\.?/i.test(cleanName)) return cleanName;
    return `Prof. ${cleanName}`;
  };

  const getAttendanceStatus = useCallback(
    (tapDate) => {
      const sessionStartMillis = getTimestampMillis(sessionData?.startTime || session?.startTime);
      const tapMillis = getTimestampMillis(tapDate);

      if (!sessionStartMillis || !tapMillis) return 'On Time';

      return tapMillis <= sessionStartMillis + 15 * 60 * 1000 ? 'On Time' : 'Late';
    },
    [sessionData, session]
  );

  const fetchSessionStatus = useCallback(async () => {
    if (!sessionId) return;

    setLoading(true);
    setSessionError('');

    try {
      const sessionRef = doc(db, 'Sessions', sessionId);
      const sessionSnap = await getDoc(sessionRef);

      if (!sessionSnap.exists()) {
        setSessionError('Session does not exist. Redirecting to session selection...');
        setTimeout(() => navigate('/'), 3000);
        return;
      }

      const data = sessionSnap.data();

      if (data.endTime) {
        setSessionError('Session has already ended. Redirecting to session selection...');
        setTimeout(() => navigate('/'), 3000);
        return;
      }

      setSessionData(data);

      if (data.selectedLab) {
        setSelectedLab((prev) => (prev !== data.selectedLab ? data.selectedLab : prev));

        const nextLabImages = data.labImages || LAB_BACKGROUND_MAP[data.selectedLab] || [BGElida];

        updateSession({
          ...session,
          ...data,
          selectedLab: data.selectedLab,
          labImage: data.labImage || '',
          labImages: nextLabImages,
        });
      }

      if (isInitialLoad) {
        setMode('Time In');
        setIsInitialLoad(false);
      }
    } catch (error) {
      console.error('Session Status Error:', error);

      if (retryCount < 3) {
        setTimeout(() => {
          setRetryCount((prev) => prev + 1);
        }, 1000 * Math.pow(2, retryCount));
      } else {
        setSessionError(`Failed to verify session after ${retryCount + 1} attempts: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  }, [sessionId, navigate, retryCount, session, updateSession, isInitialLoad]);

  const fetchEnrolledStudents = useCallback(async () => {
    if (!sessionCourse || !sessionYearLevel || !sessionSection) {
      enrolledStudentsRef.current = [];
      setEnrolledStudents([]);
      return;
    }

    try {
      const studentsQuery = query(
        collection(db, 'Students'),
        where('course', '==', sessionCourse),
        where('yearLevel', '==', sessionYearLevel),
        where('section', '==', sessionSection)
      );

      const studentsSnapshot = await getDocs(studentsQuery);

      const studentsList = studentsSnapshot.docs
        .map((d) => {
          const s = d.data();

          return {
            id: d.id,
            studentId: d.id,
            rfid: normalizeRfid(s.rfid),
            firstName: s.firstName || 'Unknown',
            lastName: s.lastName || '',
            course: s.course || '-',
            yearLevel: s.yearLevel || '-',
            section: s.section || '-',
          };
        })
        .sort((a, b) => `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`));

      enrolledStudentsRef.current = studentsList;
      setEnrolledStudents(studentsList);
    } catch (error) {
      console.error('Fetch Enrolled Students Error:', error);
      setMessage(`Failed to fetch enrolled students: ${error.message}`);
      showToast(`Failed to fetch enrolled students: ${error.message}`, 'error');
    }
  }, [sessionCourse, sessionYearLevel, sessionSection]);

  const startAttendanceListener = useCallback(() => {
    if (!sessionId) return () => {};

    if (attendanceUnsubscribeRef.current) {
      attendanceUnsubscribeRef.current();
      attendanceUnsubscribeRef.current = null;
    }

    const attendanceCollectionRef = collection(db, 'Sessions', sessionId, 'Attendance');

    const unsubscribe = onSnapshot(
      attendanceCollectionRef,
      (snapshot) => {
        const list = snapshot.docs.map((d) => {
          const data = d.data();

          return {
            id: d.id,
            ...data,
            studentId: data.studentId || d.id,
            rfid: normalizeRfid(data.rfid),
            status: data.status || '-',
            timeIn: data.timeIn || null,
            timeOut: data.timeOut || null,
          };
        });

        attendanceRef.current = list;
        setAttendance(list);
      },
      (error) => {
        console.error('Attendance Fetch Error:', error);
        setMessage(`Failed to fetch attendance data: ${error.message}`);
        showToast(`Failed to fetch attendance data: ${error.message}`, 'error');
      }
    );

    attendanceUnsubscribeRef.current = unsubscribe;
    return unsubscribe;
  }, [sessionId]);

  const upsertAttendanceLocally = useCallback((studentId, payload) => {
    setAttendance((prev) => {
      const next = [...prev];
      const index = next.findIndex(
        (item) => (item.studentId || item.id) === studentId || item.id === studentId
      );

      if (index >= 0) {
        next[index] = {
          ...next[index],
          ...payload,
          id: next[index].id || studentId,
          studentId: next[index].studentId || studentId,
          rfid: normalizeRfid(payload.rfid || next[index].rfid),
        };
        attendanceRef.current = next;
        return next;
      }

      const created = {
        id: studentId,
        studentId,
        ...payload,
        rfid: normalizeRfid(payload.rfid),
      };

      next.push(created);
      attendanceRef.current = next;
      return next;
    });
  }, []);

  const processScan = useCallback(
    async (rawValue) => {
      if (!sessionId) return;

      const rfidValue = normalizeRfid(rawValue);
      if (!rfidValue) return;

      const latestEnrolledStudents = enrolledStudentsRef.current;

      try {
        let matchedStudent = latestEnrolledStudents.find(
          (student) => normalizeRfid(student.rfid) === rfidValue
        );

        let matchedStudentId = matchedStudent?.studentId || matchedStudent?.id || '';

        if (!matchedStudent) {
          const studentsSnapshot = await getDocs(collection(db, 'Students'));
          const studentDoc = studentsSnapshot.docs.find(
            (d) => normalizeRfid(d.data().rfid) === rfidValue
          );

          if (studentDoc) {
            const studentData = studentDoc.data();

            matchedStudent = {
              id: studentDoc.id,
              studentId: studentDoc.id,
              rfid: normalizeRfid(studentData.rfid),
              firstName: studentData.firstName || 'Unknown',
              lastName: studentData.lastName || '',
              course: studentData.course || '-',
              yearLevel: studentData.yearLevel || '-',
              section: studentData.section || '-',
            };

            matchedStudentId = studentDoc.id;
          }
        }

        if (!matchedStudent) {
          const notBelongMessage = `Student not belong to ${getSectionDisplay(
            sessionCourse,
            sessionYearLevel,
            sessionSection
          )}.`;
          setMessage(notBelongMessage);
          showToast(notBelongMessage, 'error');
          return;
        }

        const isSameClass =
          matchedStudent.course === sessionCourse &&
          matchedStudent.yearLevel === sessionYearLevel &&
          matchedStudent.section === sessionSection;

        if (!isSameClass) {
          const notBelongMessage = `Student not belong to ${getSectionDisplay(
            sessionCourse,
            sessionYearLevel,
            sessionSection
          )}.`;
          setMessage(notBelongMessage);
          showToast(notBelongMessage, 'error');
          return;
        }

        const attendanceDocRef = doc(db, 'Sessions', sessionId, 'Attendance', matchedStudentId);
        const existingLocalAttendance = attendanceRef.current.find(
          (item) => (item.studentId || item.id) === matchedStudentId || item.id === matchedStudentId
        );
        const existingDocSnap = await getDoc(attendanceDocRef);
        const existingAttendance = existingDocSnap.exists()
          ? existingDocSnap.data()
          : existingLocalAttendance || null;

        const tapTime = new Date();

        if (mode === 'Time In') {
          if (existingAttendance?.timeIn) {
            setMessage(`Already recorded Time In for ${matchedStudent.firstName} ${matchedStudent.lastName}.`);
            showToast(
              `${matchedStudent.firstName} ${matchedStudent.lastName} is already recorded for Time In.`,
              'warning'
            );
            return;
          }

          const attendanceStatus = getAttendanceStatus(tapTime);

          const timeInPayload = {
            sessionId,
            studentId: matchedStudentId,
            rfid: rfidValue,
            firstName: matchedStudent.firstName || 'Unknown',
            lastName: matchedStudent.lastName || '',
            course: matchedStudent.course || '-',
            yearLevel: matchedStudent.yearLevel || '-',
            section: matchedStudent.section || '-',
            timeIn: tapTime,
            timeOut: existingAttendance?.timeOut || null,
            status: attendanceStatus,
            roomId: user?.uid || 'anonymous',
          };

          upsertAttendanceLocally(matchedStudentId, timeInPayload);
          await setDoc(attendanceDocRef, timeInPayload, { merge: true });

          const formattedName = `${matchedStudent.lastName || ''}, ${matchedStudent.firstName || 'Unknown'}`
            .replace(/^,\s*/, '')
            .trim();

          setLastStudentName(formattedName || '-');
          setLastTapTime(
            tapTime.toLocaleTimeString('en-US', {
              timeZone: 'Asia/Manila',
              hour12: false,
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
            })
          );

          setMessage(`Time In recorded for ${matchedStudent.firstName} ${matchedStudent.lastName}.`);
          showToast(
            `${matchedStudent.firstName} ${matchedStudent.lastName} timed in successfully (${attendanceStatus}).`,
            'success'
          );
          return;
        }

        if (mode === 'Time Out') {
          if (!existingAttendance?.timeIn) {
            setMessage(`No Time In record found for ${matchedStudent.firstName} ${matchedStudent.lastName}.`);
            showToast(
              `${matchedStudent.firstName} ${matchedStudent.lastName} has no Time In record yet.`,
              'error'
            );
            return;
          }

          if (existingAttendance?.timeOut) {
            setMessage(`Already recorded Time Out for ${matchedStudent.firstName} ${matchedStudent.lastName}.`);
            showToast(
              `${matchedStudent.firstName} ${matchedStudent.lastName} is already recorded for Time Out.`,
              'warning'
            );
            return;
          }

          const timeOutPayload = {
            sessionId,
            studentId: matchedStudentId,
            rfid: rfidValue,
            firstName: matchedStudent.firstName || 'Unknown',
            lastName: matchedStudent.lastName || '',
            course: matchedStudent.course || '-',
            yearLevel: matchedStudent.yearLevel || '-',
            section: matchedStudent.section || '-',
            timeIn: existingAttendance?.timeIn || null,
            timeOut: tapTime,
            status: existingAttendance?.status || '-',
            roomId: user?.uid || 'anonymous',
          };

          upsertAttendanceLocally(matchedStudentId, timeOutPayload);
          await updateDoc(attendanceDocRef, {
            timeOut: tapTime,
            roomId: user?.uid || 'anonymous',
          });

          const formattedName = `${matchedStudent.lastName || ''}, ${matchedStudent.firstName || 'Unknown'}`
            .replace(/^,\s*/, '')
            .trim();

          setLastStudentName(formattedName || '-');
          setLastTapTime(
            tapTime.toLocaleTimeString('en-US', {
              timeZone: 'Asia/Manila',
              hour12: false,
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
            })
          );

          setMessage(`Time Out recorded for ${matchedStudent.firstName} ${matchedStudent.lastName}.`);
          showToast(
            `${matchedStudent.firstName} ${matchedStudent.lastName} timed out successfully.`,
            'success'
          );
        }
      } catch (error) {
        console.error('RFID Scan Error:', error);
        setMessage(`Failed to record attendance: ${error.message}`);
        showToast(`Failed to record attendance: ${error.message}`, 'error');
      }
    },
    [
      sessionId,
      mode,
      user,
      sessionCourse,
      sessionYearLevel,
      sessionSection,
      getAttendanceStatus,
      upsertAttendanceLocally,
    ]
  );

  const handleRfidScan = useCallback(
    (e) => {
      if (!sessionId) return;

      const tag = e.target?.tagName?.toLowerCase();
      const isTypingField = tag === 'input' || tag === 'textarea' || e.target?.isContentEditable;

      if (attendanceModalOpen && isTypingField) return;

      if (scanTimeoutRef.current) {
        clearTimeout(scanTimeoutRef.current);
      }

      if (e.key === 'Enter') {
        e.preventDefault();
        const scannedValue = scanBufferRef.current.trim();
        scanBufferRef.current = '';

        if (scannedValue) {
          processScan(scannedValue);
        }
        return;
      }

      if (e.key === 'Shift' || e.key === 'Control' || e.key === 'Alt' || e.key === 'Tab') {
        return;
      }

      if (e.key && e.key.length === 1) {
        scanBufferRef.current += e.key;

        scanTimeoutRef.current = setTimeout(() => {
          scanBufferRef.current = '';
        }, 300);
      }
    },
    [sessionId, attendanceModalOpen, processScan]
  );

  useEffect(() => {
    if (!sessionId) {
      setSessionError('No session ID provided. Redirecting to session selection...');
      setTimeout(() => navigate('/'), 3000);
      setLoading(false);
      return undefined;
    }

    if (initializedRef.current) return undefined;
    initializedRef.current = true;

    let active = true;

    const init = async () => {
      await fetchSessionStatus();
      if (!active) return;
      startAttendanceListener();
    };

    init();

    return () => {
      active = false;
      if (attendanceUnsubscribeRef.current) {
        attendanceUnsubscribeRef.current();
        attendanceUnsubscribeRef.current = null;
      }
    };
  }, [sessionId, navigate, fetchSessionStatus, startAttendanceListener]);

  useEffect(() => {
    if (sessionCourse && sessionYearLevel && sessionSection) {
      fetchEnrolledStudents();
    }
  }, [sessionCourse, sessionYearLevel, sessionSection, fetchEnrolledStudents]);

  useEffect(() => {
    window.addEventListener('keydown', handleRfidScan);

    return () => {
      window.removeEventListener('keydown', handleRfidScan);
      if (scanTimeoutRef.current) clearTimeout(scanTimeoutRef.current);
    };
  }, [handleRfidScan]);

  useEffect(() => {
    if (retryCount > 0 && retryCount <= 3) {
      fetchSessionStatus();
    }
  }, [retryCount, fetchSessionStatus]);

  const handleConfirm = (confirm) => {
    if (confirm) {
      const newMode = mode === 'Time In' ? 'Time Out' : 'Time In';
      setMode(newMode);
      setLastStudentName('-');
      setLastTapTime('00:00:00');
      setMessage(`Switched to ${newMode} mode`);
      showToast(`Switched to ${newMode} mode.`, 'info');
    }
    setOpenConfirm(false);
  };

  const handleEndSession = async () => {
    try {
      await updateDoc(doc(db, 'Sessions', sessionId), { endTime: new Date() });

      updateSession({
        selectedLab: displayedLab || sessionData?.selectedLab || session?.selectedLab || '',
        labImage: currentBgImage || sessionData?.labImage || session?.labImage || '',
        labImages:
          currentLabImages && currentLabImages.length > 0
            ? currentLabImages
            : sessionData?.labImages || session?.labImages || [],
        prof: '',
        profId: '',
        profRfid: '',
        profImage: '',
        course: '',
        yearLevel: '',
        section: '',
        subject: '',
        sessionId: '',
      });

      setMessage('Session ended successfully.');
      showToast('Session ended successfully.', 'success');
      setTimeout(() => navigate('/'), 1200);
    } catch (error) {
      console.error('End Session Error:', error);
      setMessage(`Failed to end session: ${error.message}`);
      showToast(`Failed to end session: ${error.message}`, 'error');
    }
    setOpenEndConfirm(false);
  };

  const attendanceMap = useMemo(() => {
    const map = {};

    attendance.forEach((record) => {
      const rfidKey = normalizeRfid(record.rfid);
      if (!rfidKey) return;

      map[rfidKey] = {
        id: record.id,
        studentId: record.studentId || record.id,
        rfid: rfidKey,
        firstName: record.firstName || 'Unknown',
        lastName: record.lastName || '',
        course: record.course || '-',
        yearLevel: record.yearLevel || '-',
        section: record.section || '-',
        timeIn: record.timeIn || null,
        timeOut: record.timeOut || null,
        status: record.status || '-',
      };
    });

    return map;
  }, [attendance]);

  const attendanceList = useMemo(() => {
    return [...enrolledStudents]
      .map((student) => {
        const rfidKey = normalizeRfid(student.rfid);
        const attendanceEntry = attendanceMap[rfidKey];

        return {
          studentId: student.studentId || student.id || '',
          rfid: rfidKey,
          firstName: student.firstName || 'Unknown',
          lastName: student.lastName || '',
          course: student.course || '-',
          yearLevel: student.yearLevel || '-',
          section: student.section || '-',
          timeIn: attendanceEntry?.timeIn || null,
          timeOut: attendanceEntry?.timeOut || null,
          status: attendanceEntry?.status || '-',
        };
      })
      .sort((a, b) => `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`));
  }, [enrolledStudents, attendanceMap]);

  const filteredAttendance = useMemo(() => {
    return attendanceList.filter((student) =>
      `${student.firstName} ${student.lastName}`.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [attendanceList, searchQuery]);

  const totalStudents = enrolledStudents.length;
  const presentStudents = attendanceList.filter((student) =>
    mode === 'Time In' ? Boolean(student.timeIn) : Boolean(student.timeOut)
  ).length;
  const attendancePercent =
    totalStudents > 0 ? Math.round((presentStudents / totalStudents) * 100) : 0;

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRefreshAttendance = () => {
    startAttendanceListener();
    showToast('Attendance refreshed.', 'info');
  };

  const handleDownloadCSV = () => {
    try {
      const headers = ['Name', 'Course', 'Year Level', 'Section', 'Status', 'Time In', 'Time Out'];
      const rows = attendanceList.map((student) => [
        `${student.firstName} ${student.lastName}`,
        student.course || '-',
        student.yearLevel || '-',
        student.section || '-',
        student.status || '-',
        formatTimestampToTime(student.timeIn),
        formatTimestampToTime(student.timeOut),
      ]);

      const csvContent = [
        headers.map(escapeCsvValue).join(','),
        ...rows.map((row) => row.map(escapeCsvValue).join(',')),
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      saveAs(blob, `attendance_${sessionId}_${new Date().toISOString()}.csv`);
    } catch (error) {
      console.error('CSV Download Error:', error);
      setMessage(`Failed to download CSV: ${error.message}`);
      showToast(`Failed to download CSV: ${error.message}`, 'error');
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          width: '100%',
          backgroundImage: `linear-gradient(rgba(15,35,130,0.55), rgba(15,35,130,0.55)), url(${currentBgImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
        }}
      >
        <Box
          sx={{
            textAlign: 'center',
            bgcolor: 'rgba(255,255,255,0.92)',
            p: 4,
            borderRadius: 3,
            minWidth: 280,
          }}
        >
          <CircularProgress color="primary" size={60} />
          <Typography variant="h6" sx={{ mt: 2, fontWeight: 600 }}>
            Loading session data...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (sessionError) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          width: '100%',
          backgroundImage: `linear-gradient(rgba(15,35,130,0.55), rgba(15,35,130,0.55)), url(${currentBgImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
        }}
      >
        <Box
          sx={{
            bgcolor: 'rgba(255,255,255,0.92)',
            p: 4,
            borderRadius: 3,
            width: 'min(700px, 100%)',
            textAlign: 'center',
          }}
        >
          <Typography sx={{ fontSize: '1.2rem', fontWeight: 700, color: '#b00020', mb: 2 }}>
            {sessionError}
          </Typography>
          <Button variant="contained" onClick={fetchSessionStatus}>
            Retry
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        backgroundImage: `linear-gradient(rgba(15,35,130,0.55), rgba(15,35,130,0.55)), url(${currentBgImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        transition: 'background-image 0.8s ease-in-out',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 3,
        py: 4,
        position: 'relative',
      }}
    >
      <style>
        {`
          .bauble_box .bauble_label {
            background: #0050b8;
            background-position: 62px 5px;
            background-repeat: no-repeat;
            background-size: auto 5px;
            border: 0;
            border-radius: 50px;
            box-shadow: inset 0 10px 20px rgba(0,0,0,.4), 0 -1px 0 rgba(0,0,0,.2), inset 0 -1px 0 #fff;
            cursor: pointer;
            display: inline-block;
            font-size: 0;
            height: 40px;
            position: relative;
            transition: all 500ms ease;
            width: 90px;
          }

          .bauble_box .bauble_label:before {
            background-color: rgba(255,255,255,.2);
            background-position: 0 0;
            background-repeat: repeat;
            background-size: 30% auto;
            border-radius: 50%;
            box-shadow: inset 0 -5px 25px #0050b8, 0 10px 20px rgba(0,0,0,.4);
            content: '';
            display: block;
            height: 30px;
            left: 5px;
            position: absolute;
            top: 5px;
            transition: all 500ms ease;
            width: 30px;
            z-index: 2;
          }

          .bauble_box input.bauble_input {
            opacity: 0;
            position: absolute;
            pointer-events: none;
          }

          .bauble_box input.bauble_input:checked + .bauble_label {
            background-color: #c22;
          }

          .bauble_box input.bauble_input:checked + .bauble_label:before {
            background-position: 150% 0;
            box-shadow: inset 0 -5px 25px #500, 0 10px 20px rgba(0,0,0,.4);
            left: calc(100% - 35px);
          }
        `}
      </style>

      <Snackbar
        open={toastOpen}
        autoHideDuration={5000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        TransitionComponent={SlideUpTransition}
        sx={{
          '&.MuiSnackbar-root': {
            left: { xs: 16, sm: 24 },
            bottom: { xs: 16, sm: 24 },
          },
        }}
      >
        <Alert
          onClose={() => setToastOpen(false)}
          severity={toastSeverity}
          variant="filled"
          sx={{
            width: '100%',
            minWidth: { xs: 260, sm: 360 },
            maxWidth: { xs: 'calc(100vw - 32px)', sm: 460 },
            borderRadius: 2,
            boxShadow: '0 10px 24px rgba(0,0,0,0.25)',
            alignItems: 'center',
            fontFamily: 'Poppins, sans-serif',
            fontWeight: 700,
            ...(toastSeverity === 'success' && {
              bgcolor: '#0f56b3',
              color: '#fff',
              '& .MuiAlert-icon': {
                color: '#fff',
              },
            }),
            ...(toastSeverity === 'info' && {
              bgcolor: '#0f56b3',
              color: '#fff',
              '& .MuiAlert-icon': {
                color: '#fff',
              },
            }),
            ...(toastSeverity === 'warning' && {
              bgcolor: '#ffc400',
              color: '#111',
              '& .MuiAlert-icon': {
                color: '#111',
              },
            }),
            ...(toastSeverity === 'error' && {
              bgcolor: '#b90d0d',
              color: '#fff',
              '& .MuiAlert-icon': {
                color: '#fff',
              },
            }),
          }}
        >
          {toastText}
        </Alert>
      </Snackbar>

      <Box
        sx={{
          width: '100%',
          maxWidth: 880,
          borderRadius: '18px',
          overflow: 'hidden',
          border: '3px solid #d8ab16',
          boxShadow: '0 14px 40px rgba(0,0,0,0.35)',
          position: 'relative',
          top: '-60px',
          transform: 'scale(1.10)',
          transformOrigin: 'center',
        }}
      >
        <Box
          sx={{
            bgcolor: '#ffffff',
            px: { xs: 2.2, md: 5.4 },
            pt: { xs: 1.7, md: 2.5 },
            pb: { xs: 1.7, md: 2.5 },
            position: 'relative',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
            }}
          >
            <Button
              onClick={() => setOpenEndConfirm(true)}
              startIcon={<LogoutRoundedIcon />}
              sx={{
                minWidth: 0,
                px: 1.8,
                py: 0.9,
                borderRadius: '14px',
                color: '#fff',
                fontFamily: '"Space Grotesk", sans-serif',
                fontWeight: 700,
                fontSize: '0.9rem',
                textTransform: 'none',
                flexDirection: 'row-reverse',
                gap: 0.8,
                background: 'linear-gradient(135deg, rgba(255,59,59,0.95), rgba(185,13,13,0.95))',
                border: '1px solid rgba(255,255,255,0.22)',
                boxShadow: '0 10px 24px rgba(185,13,13,0.35)',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                transition: 'all 0.22s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 14px 28px rgba(185,13,13,0.45)',
                  background: 'linear-gradient(135deg, rgba(255,80,80,1), rgba(160,10,10,1))',
                },
                '&:active': {
                  transform: 'scale(0.98)',
                },
                '& .MuiButton-startIcon': {
                  marginTop: '-1px',
                  marginRight: '-4px',
                },
              }}
            >
              END
            </Button>
          </Box>

          <Box sx={{ mb: 1.7, display: 'flex', justifyContent: 'flex-start' }}>
            <Box
              component="img"
              src={SmartLabLogo}
              alt="SmartLab Logo"
              sx={{
                height: { xs: 52, md: 120 },
                width: 'auto',
                objectFit: 'contain',
                ml: '-45px',
                mt: '-30px',
              }}
            />
          </Box>

          <Box
            sx={{
              textAlign: 'center',
              mb: 1.4,
              position: 'relative',
              minHeight: { xs: 90, md: 100 },
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: '25%',
                transform: 'translateY(-50%)',
                display: 'flex',
                alignItems: 'center',
                gap: { xs: 0.8, md: 1.2 },
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: '0.96rem', md: '5rem' },
                  color: '#222',
                  fontWeight: 400,
                  whiteSpace: 'nowrap',
                  textAlign: 'left',
                  fontFamily: '"Space Grotesk", sans-serif',
                  letterSpacing: -2,
                  lineHeight: 1,
                }}
              >
                {currentTime.main}
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                  mt: { xs: 0.15, md: 0.35 },
                }}
              >
                <Typography
                  sx={{
                    fontSize: { xs: '0.62rem', md: '1.8rem' },
                    color: '#222',
                    fontWeight: 700,
                    lineHeight: 1,
                    fontFamily: '"Space Grotesk", sans-serif',
                    letterSpacing: 1.2,
                  }}
                >
                  {currentTime.seconds}
                </Typography>

                <Typography
                  sx={{
                    fontSize: { xs: '0.58rem', md: '1.5rem' },
                    color: '#222',
                    fontWeight: 800,
                    lineHeight: 1,
                    mt: { xs: 0.35, md: 0.5 },
                    fontFamily: '"Space Grotesk", sans-serif',
                    letterSpacing: 1.4,
                  }}
                >
                  {currentTime.period}
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                position: 'absolute',
                right: { xs: 8, md: 50 },
                top: '31%',
                transform: 'translateY(-50%)',
                width: { xs: 110, md: 170 },
                height: { xs: 110, md: 170 },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  width: { xs: 102, md: 158 },
                  height: { xs: 102, md: 158 },
                  borderRadius: '50%',
                  background:
                    'radial-gradient(circle at 30% 30%, rgba(255,255,255,0.95), rgba(238,243,251,0.98) 60%, rgba(220,228,242,0.92) 100%)',
                  boxShadow: `
                    0 14px 32px rgba(15, 86, 179, 0.18),
                    inset 0 3px 12px rgba(255,255,255,0.95),
                    inset 0 -8px 16px rgba(15,86,179,0.08)
                  `,
                  border: '1px solid rgba(255,255,255,0.9)',
                }}
              />

              <CircularProgress
                variant="determinate"
                value={100}
                size={150}
                thickness={3.8}
                sx={{
                  position: 'absolute',
                  color: 'rgba(15, 86, 179, 0.12)',
                  '& .MuiCircularProgress-circle': {
                    strokeLinecap: 'round',
                  },
                }}
              />

              <CircularProgress
                variant="determinate"
                value={attendancePercent}
                size={150}
                thickness={5.2}
                sx={{
                  position: 'absolute',
                  color:
                    attendancePercent >= 75
                      ? '#0f56b3'
                      : attendancePercent >= 40
                        ? '#d8ab16'
                        : '#b90d0d',
                  filter:
                    attendancePercent >= 75
                      ? 'drop-shadow(0 0 10px rgba(15,86,179,0.35))'
                      : attendancePercent >= 40
                        ? 'drop-shadow(0 0 10px rgba(216,171,22,0.35))'
                        : 'drop-shadow(0 0 10px rgba(185,13,13,0.35))',
                  transform: 'rotate(-90deg)',
                  '& .MuiCircularProgress-circle': {
                    strokeLinecap: 'round',
                    transition: 'stroke-dashoffset 0.6s ease, stroke 0.4s ease',
                  },
                }}
              />

              <Box
                sx={{
                  width: { xs: 72, md: 112 },
                  height: { xs: 72, md: 112 },
                  borderRadius: '50%',
                  background: 'linear-gradient(180deg, #ffffff 0%, #f7f9fd 100%)',
                  boxShadow: `
                    inset 0 2px 10px rgba(255,255,255,0.95),
                    inset 0 -4px 10px rgba(15,86,179,0.08),
                    0 8px 18px rgba(0,0,0,0.10)
                  `,
                  border: '2px solid rgba(255,255,255,0.95)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'absolute',
                  zIndex: 2,
                }}
              >
                <Typography
                  sx={{
                    fontSize: { xs: '0.48rem', md: '0.72rem' },
                    fontWeight: 700,
                    color: '#5d6b82',
                    lineHeight: 1,
                    fontFamily: 'Poppins, sans-serif',
                    letterSpacing: 1.4,
                    mb: 0.3,
                  }}
                >
                  ATTEND
                </Typography>

                <Typography
                  sx={{
                    fontSize: { xs: '0.88rem', md: '1.45rem' },
                    fontWeight: 900,
                    color: '#111',
                    lineHeight: 1,
                    fontFamily: '"Space Grotesk", sans-serif',
                    letterSpacing: 1.2,
                    marginTop: '3px',
                    marginBottom: '4px',
                  }}
                >
                  {presentStudents}/{totalStudents}
                </Typography>

                <Typography
                  sx={{
                    mt: 0.25,
                    fontSize: { xs: '0.56rem', md: '0.82rem' },
                    fontWeight: 800,
                    color:
                      attendancePercent >= 75
                        ? '#0f56b3'
                        : attendancePercent >= 40
                          ? '#b88900'
                          : '#b90d0d',
                    lineHeight: 1,
                    fontFamily: '"Space Grotesk", sans-serif',
                    letterSpacing: 1.3,
                  }}
                >
                  {attendancePercent}%
                </Typography>
              </Box>
            </Box>

            <Typography
              sx={{
                display: 'inline-block',
                mx: 'auto',
                px: 3.2,
                py: 0.9,
                bgcolor: '#0f56b3',
                color: '#fff',
                fontWeight: 800,
                fontSize: { xs: '1.05rem', md: '1.7rem' },
                letterSpacing: 4,
                borderRadius: '4px',
                textTransform: 'uppercase',
                flexShrink: 0,
                fontFamily: '"Space Grotesk", sans-serif',
              }}
            >
              {getSectionDisplay(sessionCourse, sessionYearLevel, sessionSection)}
            </Typography>

            <Typography
              sx={{
                mt: 0.5,
                fontStyle: 'italic',
                color: '#333',
                fontSize: { xs: '0.88rem', md: '1.2rem' },
                fontWeight: 700,
                fontFamily: 'Poppins, sans-serif',
                letterSpacing: 2,
              }}
            >
              ( {sessionSubject} )
            </Typography>

            <Typography
              sx={{
                mt: 0.85,
                fontSize: { xs: '1.25rem', md: '2rem' },
                color: '#111',
                fontWeight: 700,
                fontFamily: '"Space Grotesk", sans-serif',
                letterSpacing: 2,
              }}
            >
              {getProfessorDisplay(sessionProf)}
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: { xs: 'flex-start', md: 'center' },
              justifyContent: 'space-between',
              gap: 2.2,
              mt: 0.2,
              flexWrap: { xs: 'wrap', md: 'nowrap' },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2.2,
                flexWrap: 'nowrap',
                minWidth: 0,
                flex: 1,
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: '1.7rem', md: '3.6rem' },
                  fontWeight: 900,
                  color: '#0f56b3',
                  textTransform: 'uppercase',
                  lineHeight: 1,
                  fontFamily: 'Cyberjunkies',
                  letterSpacing: 3,
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                }}
              >
                {formatDayOnly()}
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  minWidth: 0,
                  flexShrink: 1,
                }}
              >
                <Box
                  sx={{
                    width: '4px',
                    height: 54,
                    bgcolor: '#d8ab16',
                    mr: 1.8,
                    flexShrink: 0,
                  }}
                />

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    minWidth: 0,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: { xs: '0.92rem', md: '1.14rem' },
                      color: '#111',
                      lineHeight: 1.35,
                      whiteSpace: 'nowrap',
                      fontFamily: 'Poppins, sans-serif',
                    }}
                  >
                    <Box component="span" sx={{ fontWeight: 800, mr: '40px' }}>
                      DATE:
                    </Box>{' '}
                    {formatFullDate()}
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: { xs: '0.92rem', md: '1.14rem' },
                      color: '#111',
                      lineHeight: 1.35,
                      whiteSpace: 'nowrap',
                      fontFamily: 'Poppins, sans-serif',
                    }}
                  >
                    <Box component="span" sx={{ fontWeight: 800, mr: '11px' }}>
                      SESSION:
                    </Box>{' '}
                    {formatTime(sessionTime)}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                ml: 'auto',
              }}
            >
              <Box className="bauble_box">
                <input
                  className="bauble_input"
                  id="bauble_check"
                  name="bauble"
                  type="checkbox"
                  checked={mode === 'Time Out'}
                  readOnly
                  onClick={() => setOpenConfirm(true)}
                />
                <label className="bauble_label" htmlFor="bauble_check">
                  Toggle
                </label>
              </Box>

              <Typography
                sx={{
                  fontSize: { xs: '1.12rem', md: '1.46rem' },
                  color: '#333',
                  whiteSpace: 'nowrap',
                  fontFamily: 'Poppins, sans-serif',
                  fontWeight: 700,
                }}
              >
                IN/OUT
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            bgcolor: '#0050b8',
            px: { xs: 2.2, md: 4.3 },
            py: { xs: 1.75, md: 1.95 },
            borderTop: '3px solid #d8ab16',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Box>
            <Typography
              sx={{
                color: '#ffc400',
                fontSize: { xs: '1.72rem', md: '3.4rem' },
                lineHeight: 1,
                fontWeight: 900,
                fontFamily: 'Cyberjunkies',
                textTransform: 'uppercase',
                letterSpacing: 3,
              }}
            >
              {mode === 'Time In' ? 'Welcome !' : 'Thank you !'}
            </Typography>

            <Typography
              className="advetime-font"
              sx={{
                color: '#fff',
                fontSize: { xs: '1.42rem', md: '2.12rem' },
                lineHeight: 1.1,
                mt: 0.35,
                letterSpacing: 1,
                fontFamily: 'Poppins, sans-serif',
              }}
            >
              {lastStudentName || '-'}
            </Typography>
          </Box>

          <Box
            sx={{
              minWidth: 178,
              border: '2px solid #d8ab16',
              borderRadius: '12px',
              overflow: 'hidden',
              textAlign: 'center',
              alignSelf: 'stretch',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <Box
              sx={{
                bgcolor: '#0050b8',
                color: '#fff',
                py: 0.95,
                fontWeight: 800,
                fontSize: { xs: '1.02rem', md: '1.34rem' },
                letterSpacing: 2,
                fontFamily: 'Poppins, sans-serif',
              }}
            >
              {mode === 'Time In' ? 'TIME IN' : 'TIME OUT'}
            </Box>

            <Box
              sx={{
                bgcolor: '#b90d0d',
                color: '#fff',
                py: 1.08,
                fontWeight: 700,
                fontSize: { xs: '1.24rem', md: '1.56rem' },
                fontFamily: 'Poppins, sans-serif',
                letterSpacing: 3,
              }}
            >
              {lastTapTime || '00:00:00'}
            </Box>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          position: 'fixed',
          right: 70,
          bottom: 40,
          zIndex: 1200,
          pointerEvents: 'none',
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'flex-end',
          maxWidth: 'calc(100vw - 40px)',
        }}
      >
        <Typography
          className="advetime-font"
          sx={{
            color: '#ffffff',
            fontSize: { xs: '1.2rem', md: '5rem' },
            lineHeight: 1,
            letterSpacing: 1,
            textShadow: '0 4px 14px rgba(0,0,0,0.35)',
            fontFamily: 'Advetime',
            opacity: 0.77,
            whiteSpace: 'nowrap',
            textAlign: 'right',
          }}
        >
          {displayedLab}
        </Typography>
      </Box>

      <Tooltip title="View Attendance">
        <IconButton
          onClick={() => setAttendanceModalOpen(true)}
          sx={{
            position: 'fixed',
            top: 20,
            right: 20,
            zIndex: 1300,
            bgcolor: '#ffffff',
            color: '#0050b8',
            opacity: 0.5,
            width: 58,
            height: 58,
            boxShadow: '0 8px 20px rgba(0,0,0,0.25)',
            '&:hover': { bgcolor: '#888' },
          }}
        >
          <GroupsIcon />
        </IconButton>
      </Tooltip>

      <Dialog
        open={attendanceModalOpen}
        onClose={() => setAttendanceModalOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            fontFamily: 'Poppins, sans-serif',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontFamily: '"Space Grotesk", sans-serif',
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontFamily: '"Space Grotesk", sans-serif',
              fontWeight: 700,
            }}
          >
            Attendance ( {presentStudents}/{totalStudents} )
          </Typography>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Refresh attendance data">
              <IconButton onClick={handleRefreshAttendance}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title="Download attendance as CSV">
              <IconButton onClick={handleDownloadCSV} disabled={attendanceList.length === 0}>
                <DownloadIcon />
              </IconButton>
            </Tooltip>

            <IconButton onClick={() => setAttendanceModalOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            fontFamily: 'Poppins, sans-serif',
          }}
        >
          <TextField
            label="Search by Name"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            fullWidth
            variant="outlined"
            margin="normal"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              sx: {
                fontFamily: 'Poppins, sans-serif',
              },
            }}
            InputLabelProps={{
              sx: {
                fontFamily: 'Poppins, sans-serif',
              },
            }}
          />

          <TableContainer
            component={Paper}
            sx={{
              maxHeight: 520,
              overflow: 'auto',
              borderRadius: 2,
              fontFamily: 'Poppins, sans-serif',
            }}
          >
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, fontFamily: 'Poppins, sans-serif' }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontFamily: 'Poppins, sans-serif' }}>Course</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontFamily: 'Poppins, sans-serif' }}>Year Level</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontFamily: 'Poppins, sans-serif' }}>Section</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontFamily: 'Poppins, sans-serif' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontFamily: 'Poppins, sans-serif' }}>Time In</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontFamily: 'Poppins, sans-serif' }}>Time Out</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredAttendance
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((student) => (
                    <MemoizedRow key={student.studentId || student.rfid} student={student} />
                  ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={filteredAttendance.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              '& .MuiTablePagination-toolbar, & .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiTablePagination-select, & .MuiIconButton-root':
                {
                  fontFamily: 'Poppins, sans-serif',
                },
            }}
          />
        </DialogContent>

        <DialogActions
          sx={{
            fontFamily: 'Poppins, sans-serif',
          }}
        >
          <Button
            onClick={() => setAttendanceModalOpen(false)}
            sx={{ fontFamily: 'Poppins, sans-serif' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openConfirm} onClose={() => setOpenConfirm(false)}>
        <DialogTitle>Confirm Mode Switch</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to switch to {mode === 'Time In' ? 'Time Out' : 'Time In'} mode?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleConfirm(false)}>Cancel</Button>
          <Button onClick={() => handleConfirm(true)} autoFocus>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openEndConfirm} onClose={() => setOpenEndConfirm(false)}>
        <DialogTitle>End Session</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to end this session? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEndConfirm(false)}>Cancel</Button>
          <Button onClick={handleEndSession} color="error" autoFocus>
            End Session
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NewSession;